import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { CallLog, subscribeLogsForCallID } from '@adornis/voice-assistant/call-logs.js';
import { parseRecordingPlayback, subscribeRecordingPlayback } from '../../db/recording-playback.js';
import type { BrowserVoiceCallState } from '@adornis/voice-assistant/client/browser-call.js';
import { BrowserVoiceCall } from '@adornis/voice-assistant/client/browser-call.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import arrowLeftIcon from '../assets/arrow_left.svg';
import callIcon from '../assets/call.svg';
import { callLogEventTypes, renderCallLogList, formatCallLogsAsText } from './CallLogList.js';
import { CrmLookupPanel } from './CrmLookupPanel.js';
import { DevCallerSimulationPanel } from './DevCallerSimulationPanel.js';
import { CallFeedbackPanel } from './CallFeedbackPanel.js';

export const CallPage = stateful(() => {
  const [callState, setCallState] = useState<BrowserVoiceCallState>('idle');
  const [lifecycle] = useState<{ current: BrowserVoiceCall | null; revision: number; disposed: boolean }>({
    current: null,
    revision: 0,
    disposed: false,
  });
  useEffect(() => {
    lifecycle.disposed = false;
    return () => {
      lifecycle.disposed = true;
      lifecycle.revision++;
      void lifecycle.current?.stop().catch(() => {});
      lifecycle.current = null;
    };
  }, []);
  const [callID, setCallID] = useState<string | null>(null);
  // DEV caller simulation panel: no call starts while a number change is still on its way to the server.
  const [simulationSaving, setSimulationSaving] = useState<boolean>(false);

  const callLogSub = useQuery(
    () => (callID ? subscribeLogsForCallID(callID)(selectionSet(() => CallLog, 1)) : of([])),
    [callID],
  );

  // Subscribe to recording for this callID (latest recording)
  const recordingSub = useQuery(() => (callID ? subscribeRecordingPlayback(callID)() : of(null)), [callID]);

  const startCall = async () => {
    if (lifecycle.current || lifecycle.disposed) return;
    const revision = ++lifecycle.revision;
    setCallID(null);
    setCallState('connecting');
    const newCall = new BrowserVoiceCall({
      url: '/api/browser-call/ws',
      onStateChange: state => {
        if (revision !== lifecycle.revision || lifecycle.disposed) return;
        setCallState(state);
        if (state === 'error' || state === 'stopped') lifecycle.current = null;
      },
    });
    lifecycle.current = newCall;
    try {
      const id = await newCall.start();
      if (revision === lifecycle.revision && !lifecycle.disposed) setCallID(id);
      else await newCall.stop();
    } catch {
      await newCall.stop();
      if (revision === lifecycle.revision && !lifecycle.disposed) {
        lifecycle.current = null;
        setCallState('error');
      }
    }
  };

  const stopCall = async () => {
    const current = lifecycle.current;
    lifecycle.current = null;
    lifecycle.revision++;
    await current?.stop();
    if (!lifecycle.disposed) setCallState('stopped');
  };

  const getCallButtonText = () => {
    switch (callState) {
      case 'idle':
      case 'stopped':
        return 'Anruf starten';
      case 'connecting':
        return 'Verbindung abbrechen';
      case 'connected':
        return 'Anruf beenden';
      case 'error':
        return 'Fehler - Erneut versuchen';
      default:
        return 'Anruf starten';
    }
  };

  const getCallButtonClass = () => {
    const baseClass =
      'btn rounded-full shadow-lg text-4xl transition-colors duration-200 border-none outline-none focus:ring-2';
    switch (callState) {
      case 'idle':
      case 'stopped':
      case 'error':
        return `${baseClass} bg-primary text-primary-content focus:ring-primary/50`;
      case 'connecting':
        return `${baseClass} bg-warning text-warning-content focus:ring-warning/50`;
      case 'connected':
        return `${baseClass} bg-error text-error-content focus:ring-error/50`;
      default:
        return `${baseClass} bg-primary text-primary-content focus:ring-primary/50`;
    }
  };

  const handleCallButtonClick = () => {
    if (callState === 'connected' || callState === 'connecting') {
      stopCall();
    } else if (!simulationSaving && (callState === 'idle' || callState === 'stopped' || callState === 'error')) {
      startCall();
    }
  };

  const [logTypeFilter, setLogTypeFilter] = useState<string>('all');
  const [logsCopied, setLogsCopied] = useState<boolean>(false);

  const copyLogs = async () => {
    const all = callLogSub.value ?? [];
    const filtered = all.filter((log: any) => logTypeFilter === 'all' || log.type === logTypeFilter);
    await navigator.clipboard.writeText(formatCallLogsAsText(filtered));
    setLogsCopied(true);
    setTimeout(() => setLogsCopied(false), 1500);
  };

  const renderCallLogs = () => {
    const logs = callLogSub.value;
    const filteredLogs = logs?.filter((log: any) => logTypeFilter === 'all' || log.type === logTypeFilter);

    return renderCallLogList(filteredLogs ?? []);
  };

  return html`
    <section class="w-full py-8 px-4">
      <div class="card w-full" tabindex="0">
        <div class="card-body">
          <button class="btn btn-ghost mb-4 flex items-center gap-1" @click=${() => goTo('/')}>
            <img src="${arrowLeftIcon}" alt="Zurück" class="inline w-5 h-5 mr-1" draggable="false" />
            Zurück
          </button>
          <h2 class="card-title text-primary mb-2 text-center text-2xl">Sprachanruf</h2>
          <p class="text-base-content/70 mb-6">
            Klicke auf den Button, um einen Sprachanruf direkt im Browser zu starten.
          </p>

          <!-- DEV runtime (administrators) / staging opt-in (every account): invisible unless the server enables it -->
          ${DevCallerSimulationPanel({
            callActive: callState === 'connecting' || callState === 'connected',
            onSavingChange: setSimulationSaving,
          })}

          <div class="flex flex-col items-center gap-4">
            <button
              @click=${handleCallButtonClick}
              ?disabled=${simulationSaving}
              class="${getCallButtonClass()}"
              style="width: 120px; height: 120px; min-width: 120px; min-height: 120px; aspect-ratio: 1/1;"
              aria-label="${getCallButtonText()}"
            >
              <img src="${callIcon}" alt="${getCallButtonText()}" class="w-12 h-12" draggable="false" />
            </button>

            <div class="text-center">
              <p class="text-sm text-base-content/60">
                Status: <span class="font-semibold">${getCallButtonText()}</span>
              </p>
              ${callState === 'error'
                ? html` <p class="text-sm text-error mt-1">Fehler beim Verbinden. Bitte versuchen Sie es erneut.</p> `
                : ''}
              ${callState === 'connected'
                ? html` <p class="text-sm text-success mt-1">Verbunden! Sprechen Sie jetzt.</p> `
                : ''}
            </div>

            <!-- Report issue button: copies current call ID to clipboard -->
            ${callID
              ? html`
                  <button class="btn btn-outline btn-sm mt-2" @click=${() => navigator.clipboard.writeText(callID!)}>
                    Problem melden?
                  </button>
                `
              : nothing}

            <!-- Recording player -->
            ${parseRecordingPlayback(recordingSub.value).state !== 'missing'
              ? html`<div class="w-full mt-6">
                  <h3 class="text-lg font-semibold mb-2">Aufzeichnung</h3>
                  ${parseRecordingPlayback(recordingSub.value).url
                    ? html`<audio
                        controls
                        class="w-full"
                        src="${parseRecordingPlayback(recordingSub.value).url}"
                      ></audio>`
                    : html`<p class="text-base-content/60 italic">
                        ${parseRecordingPlayback(recordingSub.value).state === 'expired'
                          ? 'Aufzeichnung nach 21 Tagen gelöscht. Das Gesprächsprotokoll bleibt verfügbar.'
                          : 'Keine Aufzeichnung verfügbar.'}
                      </p>`}
                </div>`
              : nothing}

            <!-- Feedback remains available only after the call ends. -->
            ${callID && (callState === 'stopped' || callState === 'error')
              ? html`<div class="w-full mt-6 mb-6" style="max-width: 1040px; margin-left: auto; margin-right: auto;">
                  ${CallFeedbackPanel({ callID })}
                </div>`
              : nothing}

            <!-- Live call logs -->
            ${callID
              ? html`
                  <div class="w-full mt-6 min-h-124">
                    <h3 class="text-lg font-semibold mb-2">Live Anrufprotokoll</h3>
                    <div class="flex items-center mb-2 gap-2">
                      <label class="text-sm">Filter:</label>
                      <select
                        class="select select-sm select-bordered"
                        @change=${(e: Event) => setLogTypeFilter((e.target as HTMLSelectElement).value)}
                      >
                        <option value="all" ?selected=${logTypeFilter === 'all'}>alle</option>
                        ${callLogEventTypes.map(
                          eventType =>
                            html`<option value="${eventType}" ?selected=${logTypeFilter === eventType}>
                              ${eventType}
                            </option>`,
                        )}
                      </select>
                      <button class="btn btn-xs btn-outline ml-auto" @click=${copyLogs}>
                        ${logsCopied ? '✓ Kopiert!' : '📋 Logs kopieren'}
                      </button>
                    </div>
                    <div class="bg-base-200 rounded-lg p-4 min-h-112 max-h-[75vh] overflow-y-auto">
                      ${renderCallLogs()}
                    </div>
                  </div>
                `
              : nothing}
          </div>
        </div>
      </div>

      <!-- CRM-Suche (CRM Lookup) — sibling card below the call card -->
      ${CrmLookupPanel()}
    </section>
  `;
});
